/* Canvello — content pages (Tasks 6.1, 6.2).
 *
 * <cv-map> (sections/cv-showroom-find.liquid): the map panel. Nothing is
 *   loaded from Google until "Show the map" is pressed; then Google Maps'
 *   keyless embed for the address replaces the drawn panel, and focus moves
 *   to it. Without JS the panel keeps its two "Open in…" links.
 *
 * [data-cv-visit-rows] (sections/cv-showroom-visit.liquid): the linen
 *   column reads back the visit, the day and the time as they are picked.
 *   The date field's minimum is the visitor's own today (the page may be
 *   cached from another day).
 *
 * <cv-faq> (sections/cv-faq.liquid, Task 6.2): shows the search field
 *   (hidden without JS) and filters the questions as you type, by question
 *   and answer, ignoring case and accents; subject groups with nothing left
 *   are hidden, the count is said (polite live region), and "Nothing
 *   matches" shows when nothing is left. Escape clears the field. The
 *   questions stay <details>, so opening them needs no script.
 */
(() => {
  if (!customElements.get('cv-map')) {
    customElements.define(
      'cv-map',
      class extends HTMLElement {
        connectedCallback() {
          const show = this.querySelector('[data-cv-map-show]');
          const note = this.querySelector('[data-cv-map-note]');
          if (!show || !this.dataset.src) return;
          show.hidden = false;
          if (note) note.hidden = false;
          show.addEventListener('click', () => this.load(), { once: true });
        }

        load() {
          const frame = document.createElement('iframe');
          frame.src = this.dataset.src;
          frame.title = this.dataset.title || '';
          frame.loading = 'lazy';
          frame.referrerPolicy = 'no-referrer-when-downgrade';
          frame.className = 'cv-pgf__frame';
          frame.tabIndex = 0;
          this.querySelector('[data-cv-map-show]')?.remove();
          this.querySelector('[data-cv-map-note]')?.remove();
          this.prepend(frame);
          this.setAttribute('data-loaded', '');
          frame.focus({ preventScroll: true });
        }
      }
    );
  }

  if (!customElements.get('cv-faq')) {
    const fold = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    customElements.define(
      'cv-faq',
      class extends HTMLElement {
        connectedCallback() {
          const box = this.querySelector('[data-cv-faq-search]');
          this.field = box && box.querySelector('input');
          this.count = this.querySelector('[data-cv-faq-count]');
          this.none = this.querySelector('[data-cv-faq-none]');
          if (!this.field || this.cvReady) return;
          this.cvReady = true;
          this.items = [...this.querySelectorAll('[data-cv-faq-item]')].map((el) => ({ el, text: fold(el.textContent) }));
          box.hidden = false;
          let timer = 0;
          this.field.addEventListener('input', () => {
            clearTimeout(timer);
            timer = setTimeout(() => this.filter(), 120);
          });
          this.field.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.field.value) {
              e.preventDefault();
              this.field.value = '';
              this.filter();
            }
          });
        }

        filter() {
          const words = fold(this.field.value).split(/\s+/).filter(Boolean);
          let left = 0;
          for (const it of this.items) {
            const hit = words.every((w) => it.text.includes(w));
            it.el.hidden = !hit;
            if (hit) left += 1;
          }
          this.querySelectorAll('[data-cv-faq-group]').forEach((g) => {
            g.hidden = !g.querySelector('[data-cv-faq-item]:not([hidden])');
          });
          if (this.none) this.none.hidden = left > 0;
          if (!this.count) return;
          if (!words.length) this.count.textContent = '';
          else if (left === 0) this.count.textContent = this.dataset.tNone || '';
          else if (left === 1) this.count.textContent = this.dataset.tOne || '1';
          else this.count.textContent = (this.dataset.tMany || '%n%').replace('%n%', left);
        }
      }
    );
  }

  const pad = (n) => String(n).padStart(2, '0');

  function visit(form) {
    const rows = form.querySelector('[data-cv-visit-rows]');
    if (!rows) return;
    const day = form.querySelector('[data-cv-visit-pick="day"]');
    if (day) {
      const d = new Date();
      day.min = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
    const lang = document.documentElement.lang || undefined;
    const read = () => {
      for (const part of ['kind', 'time']) {
        const on = form.querySelector(`[data-cv-visit-pick="${part}"]:checked`);
        const out = rows.querySelector(`[data-cv-visit-show="${part}"]`);
        if (out) out.textContent = on ? on.closest('label').querySelector('[data-label]').textContent.trim() : '—';
      }
      const out = rows.querySelector('[data-cv-visit-show="day"]');
      if (out && day) {
        // The field's value is a calendar date: read it at noon, locally,
        // so no time zone moves it to the day before.
        const v = day.value ? new Date(`${day.value}T12:00:00`) : null;
        out.textContent = v && !isNaN(v)
          ? v.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'long' })
          : out.dataset.empty || '—';
      }
    };
    form.addEventListener('change', read);
    form.addEventListener('input', read);
    read();
    rows.hidden = false;
  }

  const init = () => document.querySelectorAll('.cv-pgv__form').forEach(visit);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
